import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { NoteInfo } from "./session-artifact-note-renderer.utils";
import { getLastNoteArtifactFromItem, isLastNoteInSession } from "../../session-item";
import { SessionPartConfig, SessionPartContext } from "../session-part.types";
import { MDSSessionArtifactPreviewNoteRenderer } from "./session-artifact-preview-note-renderer";
import { useMDSSessionItem } from "@/mds/components/sessions/session-context/mds-session-context.hooks";
import { SessionArtifactDetailsRenderer } from "@/mds/components/sessions/session-parts/artifacts/session-artifact-details-renderer";

interface Props {
  context: SessionPartContext;
  config: SessionPartConfig;
  noteInfo: NoteInfo;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}

export const MDSSessionArtifactNoteEmbed: FC<Props> = ({ context, config, noteInfo, onClick }) => {
  const item = useMDSSessionItem({ itemId: context.itemId });

  // Check if this is the last note in the session
  const lastArtifactId =
    context.lastArtifactId &&
    item &&
    isLastNoteInSession({ sessionItem: item, lastArtifactId: context.lastArtifactId });

  const lastArtifact = useMemo(
    () => (lastArtifactId && item ? getLastNoteArtifactFromItem(item) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastArtifactId, !!item]
  );

  // If this is the last note, render with full editor
  if (lastArtifactId && lastArtifact) {
    return (
      <PreviewContainer>
        <SessionArtifactDetailsRenderer artifactPart={lastArtifact} config={config} />
        <MDSSessionArtifactPreviewNoteRenderer artifactPart={lastArtifact} onClick={onClick} />
      </PreviewContainer>
    );
  }

  return (
    <StyledContainer onClick={onClick}>
      <StyledIconWrapper>
        <MdsIcon kind={MdsIconKind.Document} />
      </StyledIconWrapper>
      <StyledContentColumn>
        <StyledPrimaryLabel>{noteInfo.primaryLabel}</StyledPrimaryLabel>
        {noteInfo.secondaryLabel && (
          <StyledSecondaryLabel>{noteInfo.secondaryLabel}</StyledSecondaryLabel>
        )}
      </StyledContentColumn>
    </StyledContainer>
  );
};

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: ${({ theme }) => theme.spacing.xs};
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) =>
    `${theme.spacing[4]} ${theme.spacing[6]} ${theme.spacing[4]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background-color: ${({ theme }) => theme.colors.surface.preview};
  min-width: 300px;
  max-width: 300px;
  user-select: none;
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  transition: 0.08s all;

  &:hover {
    border-color: ${({ theme, onClick }) =>
      onClick ? theme.colors.grey.x200 : theme.colors.grey.x100};
    box-shadow: ${({ onClick }) => (onClick ? "0px 1px 2px 0px rgba(0, 0, 0, 0.02)" : "none")};
  }

  &:active {
    border-color: ${({ theme, onClick }) =>
      onClick ? theme.colors.grey.x200 : theme.colors.grey.x100};
    box-shadow: ${({ onClick }) => (onClick ? "0px 1px 1px 0px rgba(0, 0, 0, 0.06)" : "none")};
  }
`;

const StyledIconWrapper = styled.div`
  width: 16px;
  height: 19px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.icon.placeholder};
  }
`;

const StyledContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};
  flex: 1;
  min-width: 0;
`;

const StyledPrimaryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  color: ${({ theme }) => theme.colors.text.contrast};
  font-weight: ${({ theme }) => theme.fontWeights.smedium};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledSecondaryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
